import { Controller } from "@hotwired/stimulus"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"
import { showToast } from "../utils/show_toast"
import { csrfToken } from "../utils/csrf_token"

const DEFAULT_ZOOM = 15

export default class extends Controller {
  static values = {
    apiKey: String,
    mapId: String,
    latitude: Number,
    longitude: Number,
    editable: Boolean,
    zoom: { type: Number, default: DEFAULT_ZOOM },
    hasInitialCoords: { type: Boolean, default: true },
    originalStreet: String,
    originalCityId: String,
    originalState: String,
    originalPostalCode: String,
    originalLatitude: String,
    originalLongitude: String
  }

  static targets = [
    "mapContainer",
    "latitudeInput",
    "longitudeInput",
    "street1Input",
    "citySelect",
    "stateSelect",
    "postalCodeInput",
    "placeholder",
    "findOnMapButton",
    "findOnMapButtonText",
    "findOnMapButtonSpinner"
  ]

  connect() {
    if (!this.apiKeyValue) return
    if (!this.editableValue && (!this.latitudeValue || !this.longitudeValue)) return
    this.loadMap()
  }

  disconnect() {
    this.removePlacementClickListener()
    if (this.map && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.map)
    }
  }

  // Google Maps paints at 0×0 if the container was display:none; resize after it is shown.
  relayout() {
    if (!this.map) {
      this.loadMap()
      return
    }

    window.google?.maps?.event?.trigger(this.map, "resize")
    const center = this.map.getCenter()
    if (center) this.map.setCenter(center)
  }

  get zoomLevel() {
    return this.hasZoomValue ? this.zoomValue : DEFAULT_ZOOM
  }

  get mapContainerElement() {
    return this.hasMapContainerTarget ? this.mapContainerTarget : this.element
  }

  async loadMap() {
    if (!window.google?.maps && !document.querySelector('script[src*="maps.googleapis.com"]')) {
      setOptions({ key: this.apiKeyValue })
    }
    const { Map } = await importLibrary("maps")
    const { AdvancedMarkerElement } = await importLibrary("marker")
    this.initMap(Map, AdvancedMarkerElement)
  }

  initMap(Map, AdvancedMarkerElement) {
    this.AdvancedMarkerElement = AdvancedMarkerElement
    const position = { lat: this.latitudeValue, lng: this.longitudeValue }

    this.map = new Map(this.mapContainerElement, {
      center: position,
      zoom: this.zoomLevel,
      mapId: this.mapIdValue,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true
    })

    if (this.editableValue) {
      this.setupEditableMap(AdvancedMarkerElement, position)
    } else {
      new AdvancedMarkerElement({ position, map: this.map, title: "Location" })
    }
  }

  setupEditableMap(AdvancedMarkerElement, position) {
    this.initialPosition = { lat: position.lat, lng: position.lng }

    if (this.hasInitialCoordsValue) {
      this.marker = this.createDraggableMarker(AdvancedMarkerElement, position)
    } else {
      this.marker = null
      this.addPlacementClickListener(AdvancedMarkerElement)
    }
  }

  addPlacementClickListener(AdvancedMarkerElement) {
    this.removePlacementClickListener()
    this.placementClickListener = this.map.addListener("click", (e) => this.placeMarkerAtClick(e, AdvancedMarkerElement))
  }

  removePlacementClickListener() {
    if (this.placementClickListener) {
      this.placementClickListener.remove()
      this.placementClickListener = null
    }
  }

  createDraggableMarker(AdvancedMarkerElement, position) {
    const marker = new AdvancedMarkerElement({
      position,
      map: this.map,
      title: "Location",
      gmpDraggable: true
    })
    marker.addListener("dragend", () => this.syncFormFromMarker())
    return marker
  }

  placeMarkerAtClick(e, AdvancedMarkerElement) {
    const position = e.latLng

    if (this.marker) {
      this.marker.position = position
    } else {
      this.marker = this.createDraggableMarker(AdvancedMarkerElement, position)
      this.initialPosition = { lat: position.lat(), lng: position.lng() }
      this.removePlacementClickListener()
    }

    this.syncFormFromMarker()
  }

  syncFormFromMarker() {
    if (!this.marker) return
    if (!this.hasLatitudeInputTarget || !this.hasLongitudeInputTarget) return

    const { lat, lng } = this.extractLatLng(this.marker.position)
    this.latitudeInputTarget.value = lat.toFixed(6)
    this.longitudeInputTarget.value = lng.toFixed(6)
  }

  extractLatLng(position) {
    const lat = typeof position.lat === "function" ? position.lat() : position.lat
    const lng = typeof position.lng === "function" ? position.lng() : position.lng
    return { lat, lng }
  }

  selectValueOrEmpty(selectElement) {
    if (!selectElement?.options?.length) return ""
    const opt = selectElement.options[selectElement.selectedIndex]
    if (!opt || opt.value === "") return ""
    return opt.text?.trim() || ""
  }

  resetPin() {
    if (!this.marker || !this.initialPosition) return

    this.marker.position = this.initialPosition
    this.map.setCenter(this.initialPosition)
    this.map.setZoom(this.zoomLevel)
    this.syncFormFromMarker()
  }

  async geocodeAddress(address) {
    const res = await fetch("/administration/geocode", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken() || ""
      },
      body: JSON.stringify({ address })
    })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data.lat != null && data.lng != null) {
      return { lat: data.lat, lng: data.lng }
    }

    const { Geocoder } = await importLibrary("geocoding")
    const geocoder = new Geocoder()
    const response = await new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        resolve({ results: results || [], status })
      })
    })
    if (response.status !== "OK" || !response.results?.length) return null

    const location = response.results[0]?.geometry?.location
    if (!location) return null

    return this.extractLatLng(location)
  }

  async geocodeFromAddress() {
    if (!this.hasStreet1InputTarget || !this.hasCitySelectTarget || !this.hasStateSelectTarget || !this.hasPostalCodeInputTarget) {
      return
    }

    const street = this.street1InputTarget.value?.trim()
    const city = this.selectValueOrEmpty(this.citySelectTarget)
    const state = this.selectValueOrEmpty(this.stateSelectTarget)
    const postalCode = this.postalCodeInputTarget.value?.trim()

    const parts = [street, city, state, postalCode].filter(Boolean)
    if (parts.length < 3) {
      if (this.hasLatitudeInputTarget && this.hasLongitudeInputTarget) {
        const lat = parseFloat(this.latitudeInputTarget.value)
        const lng = parseFloat(this.longitudeInputTarget.value)
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          this.latitudeInputTarget.value = lat.toFixed(6)
          this.longitudeInputTarget.value = lng.toFixed(6)
          this.updateMapFromCoordinates(lat, lng)
          this.showGeocodeSuccessToast()
          return
        }
      }
      this.showGeocodeErrorToast()
      this.resetToPlaceholder()
      return
    }

    const address = parts.join(", ")

    this.setFindOnMapLoading(true)
    try {
      const latLng = await this.geocodeAddress(address)
      if (!latLng) {
        this.showGeocodeErrorToast()
        this.resetToPlaceholder()
        return
      }

      const { lat, lng } = latLng

      if (this.hasLatitudeInputTarget) this.latitudeInputTarget.value = lat.toFixed(6)
      if (this.hasLongitudeInputTarget) this.longitudeInputTarget.value = lng.toFixed(6)

      this.updateMapFromCoordinates(lat, lng)
      this.showGeocodeSuccessToast()
    } finally {
      this.setFindOnMapLoading(false)
    }
  }

  showGeocodeErrorToast() {
    showToast("Location could not be found for the given address or coordinates.", "error", { duration: 3000 })
  }

  showGeocodeSuccessToast() {
    showToast("Location found and map updated.", "success")
  }

  setFindOnMapLoading(loading) {
    if (!this.hasFindOnMapButtonTarget) return
    this.findOnMapButtonTarget.disabled = loading
    if (this.hasFindOnMapButtonTextTarget) {
      this.findOnMapButtonTextTarget.classList.toggle("hidden", loading)
    }
    if (this.hasFindOnMapButtonSpinnerTarget) {
      this.findOnMapButtonSpinnerTarget.classList.toggle("hidden", !loading)
    }
  }

  clearAddressAndMap() {
    if (this.hasStreet1InputTarget) this.street1InputTarget.value = ""
    if (this.hasCitySelectTarget) this.clearCustomSelect(this.citySelectTarget)
    if (this.hasPostalCodeInputTarget) this.postalCodeInputTarget.value = ""
    if (this.hasLatitudeInputTarget) this.latitudeInputTarget.value = ""
    if (this.hasLongitudeInputTarget) this.longitudeInputTarget.value = ""
    this.resetToPlaceholder()
  }

  clearCustomSelect(selectElement) {
    if (!selectElement) return
    const wrapper = selectElement.closest("[data-controller*='custom-select']")
    const controller = this.application.getControllerForElementAndIdentifier(wrapper, "custom-select")
    controller?.clear()
  }

  setCustomSelectValue(selectElement, value) {
    if (!selectElement) return
    const wrapper = selectElement.closest("[data-controller*='custom-select']")
    const controller = this.application.getControllerForElementAndIdentifier(wrapper, "custom-select")
    if (!controller) return
    controller.nativeSelectTarget.value = value ?? ""
    controller.updateLabel()
    controller.highlightSelected(value ?? "")
    if (controller.hasClearBtnTarget) {
      controller.clearBtnTarget.classList.toggle("hidden", !value)
    }
  }

  restoreOriginalAddress() {
    if (this.hasStreet1InputTarget) this.street1InputTarget.value = this.originalStreetValue ?? ""
    if (this.hasCitySelectTarget) this.setCustomSelectValue(this.citySelectTarget, this.originalCityIdValue)
    if (this.hasStateSelectTarget) this.setCustomSelectValue(this.stateSelectTarget, this.originalStateValue)
    if (this.hasPostalCodeInputTarget) this.postalCodeInputTarget.value = this.originalPostalCodeValue ?? ""
    if (this.hasLatitudeInputTarget) this.latitudeInputTarget.value = this.originalLatitudeValue ?? ""
    if (this.hasLongitudeInputTarget) this.longitudeInputTarget.value = this.originalLongitudeValue ?? ""

    const lat = parseFloat(this.originalLatitudeValue)
    const lng = parseFloat(this.originalLongitudeValue)
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      this.updateMapFromCoordinates(lat, lng)
    } else {
      this.resetToPlaceholder()
    }
    showToast("Address and coordinates have been restored.", "success")
  }

  resetToPlaceholder() {
    if (!this.map || !this.AdvancedMarkerElement) return

    if (this.marker) {
      this.marker.map = null
      this.marker = null
    }
    this.initialPosition = null

    if (this.editableValue) {
      this.addPlacementClickListener(this.AdvancedMarkerElement)
    }

    const defaultCenter = { lat: this.latitudeValue, lng: this.longitudeValue }
    this.map.setCenter(defaultCenter)
    this.map.setZoom(this.zoomLevel)

    if (this.hasLatitudeInputTarget) this.latitudeInputTarget.value = ""
    if (this.hasLongitudeInputTarget) this.longitudeInputTarget.value = ""

    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
  }

  updateMapFromCoordinates(lat, lng) {
    if (!this.map || !this.AdvancedMarkerElement) return

    const position = { lat, lng }
    this.map.setCenter(position)
    this.map.setZoom(this.zoomLevel)

    if (this.marker) {
      this.marker.position = position
    } else {
      this.marker = this.createDraggableMarker(this.AdvancedMarkerElement, position)
      this.initialPosition = { ...position }
    }

    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
  }

  syncMapFromInputs() {
    if (!this.hasLatitudeInputTarget || !this.hasLongitudeInputTarget) return

    const lat = parseFloat(this.latitudeInputTarget.value)
    const lng = parseFloat(this.longitudeInputTarget.value)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return

    this.updateMapFromCoordinates(lat, lng)
  }
}
